import { getAuthorized } from "@/lib/fetch";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "react-router-dom";
import { DisplayParams } from "../../../data/displayParams";

type UseTableProps<TData> = {
  client: string;
  table_name: string;
  pagesizeDefault?: number;
  optionalParams?: Partial<TData>;
  audit_id?: number;
};

export function useTable<TData>({
  client,
  table_name,
  pagesizeDefault = DisplayParams.DEFAULT_PAGE_SIZE_DEFAULT,
  audit_id = undefined,
}: UseTableProps<TData>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const queryParamsObj: Record<string, string | number | undefined> = {};
  queryParams.forEach((value, key) => {
    queryParamsObj[key] = value;
  });

  if (audit_id) {
    queryParamsObj["audit_id"] = audit_id;
  }

  const currentPage = Number(searchParams.get("page")) || 1;
  const pagesize = Number(searchParams.get("pagesize")) || pagesizeDefault;

  const setCurrentPage = (value: React.SetStateAction<number>) => {
    const newPage = typeof value === "function" ? value(currentPage) : value;
    const newSearchParams = new URLSearchParams();

    searchParams.forEach((value, key) => {
      if (key !== "page" && key !== "pagesize") {
        newSearchParams.append(key, value);
      }
    });

    newSearchParams.set("page", newPage.toString());
    newSearchParams.set("pagesize", pagesize.toString());

    setSearchParams(newSearchParams);
  };

  const { isLoading, error, data } = useQuery({
    queryKey: [client, table_name, "get", queryParamsObj],
    queryFn: () =>
      getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${client}/${table_name}?` +
          buildQueryString(queryParamsObj),
      ).then((res) => res.json() as Promise<{ data: TData[]; count: number }>),
  });

  return {
    data,
    isLoading,
    error,
    currentPage,
    pagesize,
    setCurrentPage,
  };
}

const buildQueryString = <TData extends Record<string, any>>(
  queryParams: Readonly<Partial<Record<string, string | number | undefined>>>,
): string => {
  return Object.keys(queryParams)
    .map(
      (key) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(
          queryParams[key as keyof TData] as string,
        )}`,
    )
    .join("&");
};
